import React, { useState, useEffect } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { firestore } from "../../firebase";
import { useLanguage } from "../../LanguageContext";
import * as text from "../../translation.json";
import LocationQuickEditPanel from "./editLocationComponents/locationQuickEditPanel";
import "../../css/quickReviewLocations.css";

const auth = getAuth();
const db = firestore;

// Only visible to this account — hidden from the sidebar on purpose, reached
// only via a direct link (/MyBusiness/quickReview).
const ALLOWED_EMAIL = "steffen@flyteori.no";


// Faster review workflow: list every location for the business, and let you
// expand one at a time into a single page containing About + Menu + Reviews
// together (instead of editMyLocation.js's tab-per-section flow), so you can
// scan for missing info and save & publish without navigating away.
const QuickReviewLocations = ({ currentBusinessID }) => {
  const { language } = useLanguage();
  const [currentUserEmail, setCurrentUserEmail] = useState(undefined);
  const [userInformation, setUserInformation] = useState(null);
  const [locationsInfo, setLocationsInfo] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openLocationIds, setOpenLocationIds] = useState(() => new Set());

  useEffect(() => {
    auth.onAuthStateChanged(async function (user) {
      setCurrentUserEmail(user ? user.email : null);
      if (user) {
        const userInfo = await getDoc(doc(db, "users", user.uid));
        setUserInformation(userInfo.data());
      }
    });
  }, []);

  useEffect(() => {
    const isValidID =
      currentBusinessID &&
      typeof currentBusinessID === "string" &&
      currentBusinessID.length > 0;

    if (!isValidID) return;

    const loadLocations = async () => {
      const businessDoc = await getDoc(doc(db, "businesses", currentBusinessID));
      const menus = businessDoc.data()?.menus || [];

      const fetchedData = await Promise.all(
        menus.map(async (id) => await getDoc(doc(db, "lokasjoner", id))),
      );

      setLocationsInfo(fetchedData);
      setLoaded(true);
    };

    loadLocations();
  }, [currentBusinessID]);

  const toggleLocation = (id) => {
    setOpenLocationIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const refreshLocation = async (id) => {
    const snap = await getDoc(doc(db, "lokasjoner", id));
    setLocationsInfo((prev) => prev.map((l) => (l.id === id ? snap : l)));
  };

  if (currentUserEmail === undefined) {
    return null;
  }

  if (currentUserEmail !== ALLOWED_EMAIL) {
    return <>Access Denied!</>;
  }

  const computeFlags = (data) => {
    const aboutFlagged =
      !data?.mainProfileImage &&
      !(data?.profileImages || []).some((x) => x) &&
      (!data?.aboutBusiness || (data?.tags || []).length === 0);
    const menuFlagged = !data?.menus || data.menus.length === 0;
    const reviewsFlagged =
      !data?.tripadvisorLink && !data?.yelpLink && !data?.googleId;
    return { aboutFlagged, menuFlagged, reviewsFlagged };
  };

  return (
    <div>
      <h1>{text[language].mylocations} — Quick Review</h1>
      {!loaded ? (
        <p>Loading...</p>
      ) : locationsInfo.length === 0 ? (
        <p>No locations to show</p>
      ) : (
        locationsInfo.map((locationSnap) => {
          const data = locationSnap.data();
          const { aboutFlagged, menuFlagged, reviewsFlagged } = computeFlags(data);
          const isSelected = openLocationIds.has(locationSnap.id);

          return (
            <div key={locationSnap.id} style={{ marginBottom: "15px" }}>
              <div
                className={`box quickReviewRow${isSelected ? " quickReviewRowOpen" : ""}`}
                onClick={() => toggleLocation(locationSnap.id)}>
                <div className='businessOverView'>
                  <label className='locationNameLabel'>
                    {data?.locationName}
                  </label>
                  <label
                    style={{ color: data?.active ? "#52c78c" : "#f9615b" }}>
                    {data?.active ? text[language].active : text[language].notactive}
                  </label>
                  <label>{data?.adress ? data.adress : "-"}</label>
                  {userInformation?.admin && (
                    <label>
                      {data?.menusChanged
                        ? new Date(data.menusChanged).toLocaleString("nb-NO")
                        : null}
                    </label>
                  )}
                  <div className='quickReviewFlags'>
                    <span
                      className={`quickReviewFlagDot${aboutFlagged ? " flagged" : ""}`}
                      title='About'>
                      A
                    </span>
                    <span
                      className={`quickReviewFlagDot${menuFlagged ? " flagged" : ""}`}
                      title='Menu'>
                      M
                    </span>
                    <span
                      className={`quickReviewFlagDot${reviewsFlagged ? " flagged" : ""}`}
                      title='Reviews'>
                      R
                    </span>
                  </div>
                  <a href='#' className='universalButtonRed'>
                    {isSelected ? "Close" : text[language].edit}
                  </a>
                </div>
              </div>
              {isSelected && (
                <LocationQuickEditPanel
                  key={locationSnap.id}
                  locationId={locationSnap.id}
                  currentBusinessID={currentBusinessID}
                  userInformation={userInformation}
                  onSaved={() => refreshLocation(locationSnap.id)}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default QuickReviewLocations;
